import Button from '../Button';
import Typography, { TypographyStyle } from '../Typography/Typography';
import clsx from 'clsx';
import { Link } from '~/shared/components';
import { useMemo } from 'react';
import { ButtonColorProps } from '~/lib';
import styles from './HeroContent.module.scss';
import { CustomCSSProperties } from '~/templates/blocks/components/M11Hero/M11Hero';

interface HeroContentProps {
    trumpet?: string;
    title?: string;
    titleSize?: string;
    paragraphText?: string;
    link?: {
        text?: string;
        title?: string;
        url?: string;
        id?: string;
    };
    foregroundColor: string;
    customProperties?: CustomCSSProperties;
    noMedia?: boolean;
}

export const HeroContent = ({
    trumpet,
    paragraphText,
    link,
    title,
    foregroundColor,
    titleSize,
    customProperties,
    noMedia,
}: HeroContentProps) => {
    const headingVariant = useMemo<TypographyStyle>(() => {
        switch (titleSize) {
            case 'Large':
                return 'display-L';
            case 'Extra Large':
                return 'display-XL';
        }

        return 'title-L';
    }, [titleSize]);

    const buttonVariant = useMemo<ButtonColorProps>(() => {
        const whiteValues = ['#fff', 'white', '#ffffff'];

        if (whiteValues.includes(foregroundColor.toLowerCase())) return 'primary-inverse';

        return 'primary';
    }, [foregroundColor]);

    return (
        <div
            className={clsx(styles.content, {
                [styles.noImage]: noMedia,
            })}
            style={customProperties}
        >
            {trumpet && <span className={styles.trumpet}>{trumpet}</span>}

            {title && (
                <Typography
                    className={clsx(styles.title, {
                        [styles.noParagraph]: !paragraphText,
                        [styles.noSpace]: !paragraphText || !link?.text,
                        [styles.withButton]: !paragraphText && link?.text,
                    })}
                    variant={headingVariant}
                    as="h2"
                >
                    <span>{title}</span>
                </Typography>
            )}

            {paragraphText && (
                <div
                    className={clsx(styles.paragraph, {
                        [styles.noSpace]: !link?.text,
                    })}
                    dangerouslySetInnerHTML={{ __html: paragraphText }}
                />
            )}

            {link?.text && link?.url && (
                <Button
                    variant={buttonVariant}
                    title={link.title}
                    as={Link}
                    href={link.url}
                    children={link.text}
                    className={styles.cta}
                />
            )}
        </div>
    );
};
